import { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import Avatar from '../common/Avatar';
import FaIcon from '../common/FaIcon';
import { SettingsIcon } from '../icons';

interface AccountMenuProps {
  /** Show the "Claim ICS position" entry — an incident is engaged but the
      user holds no position for it. */
  showClaimPosition: boolean;
  onClaimPosition: () => void;
}

/**
 * Avatar/account dropdown for the desktop status bar. Hosts the account
 * settings link and, when no ICS position is claimed for the engaged
 * incident, the "Claim position" affordance (claimed positions render as a
 * mono chip directly in the bar instead).
 */
export default function AccountMenu({ showClaimPosition, onClaimPosition }: AccountMenuProps) {
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        // Keyboard contract: focus returns to the trigger, otherwise it is
        // dropped on <body> when a menu item had focus.
        triggerRef.current?.focus();
        return;
      }
      // Roving focus: ArrowDown/ArrowUp cycle through menu items (wrapping);
      // Home/End jump to first/last. Enter activates the focused item natively.
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
        const items = Array.from(
          menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
        );
        if (items.length === 0) return;
        e.preventDefault();
        const currentIndex = items.indexOf(document.activeElement as HTMLElement);
        let nextIndex: number;
        if (e.key === 'Home') nextIndex = 0;
        else if (e.key === 'End') nextIndex = items.length - 1;
        else if (e.key === 'ArrowDown') nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
        else nextIndex = currentIndex < 0 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
        items[nextIndex]?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      {/* Raw <button> — dropdown trigger (allowed per Raw HTML Element Policy) */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex items-center gap-2 rounded-lg p-1 pr-1.5 hover:bg-surface-tertiary transition-colors"
      >
        <Avatar src={user?.avatar_url} name={user?.name} size="sm" />
        <span
          className="hidden xl:inline text-sm font-medium text-theme-primary truncate max-w-[140px]"
          title={user?.name || 'User'}
        >
          {user?.name || 'User'}
        </span>
        <FaIcon
          icon="chevron-down"
          className={`h-3 w-3 text-theme-tertiary transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 w-64 rounded-lg border border-theme-primary bg-surface-primary py-1 shadow-lg z-50"
        >
          <div className="px-3 py-2 border-b border-theme-primary">
            <p className="text-sm font-medium text-theme-primary truncate">{user?.name || 'User'}</p>
            {user?.email && <p className="text-xs text-theme-tertiary truncate">{user.email}</p>}
          </div>

          {showClaimPosition && (
            /* Raw <button> — context-menu item (allowed per Raw HTML Element Policy) */
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onClaimPosition();
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-theme-primary hover:bg-surface-tertiary transition-colors"
            >
              <FaIcon icon="arrows-rotate" className="h-4 w-4 shrink-0 text-theme-tertiary" />
              <span className="min-w-0">
                Claim ICS position
                <span className="block text-xs text-theme-tertiary">Position: No role</span>
              </span>
            </button>
          )}

          <NavLink
            to="/account"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-2 text-sm text-theme-primary hover:bg-surface-tertiary transition-colors"
          >
            <SettingsIcon className="h-4 w-4 shrink-0 text-theme-tertiary" />
            Account settings
          </NavLink>
        </div>
      )}
    </div>
  );
}
